import { Injectable, signal } from '@angular/core';
import { FormControl } from '@angular/forms';

import { CreateLayerDtoWithoutProject } from '@connect-services/client/services/layer-creation-form.service';
import { UrlSourceEnum, UrlSourceLayer, UrlSourceService } from '@connect-services/client/services/url-source.service';
import { Layer, LayerOptions, ServiceCatalog } from '@connect-services/src/sdk/api';
import WfsExtensionEnum = LayerOptions.WfsExtensionEnum;

import type { FluxLayerType, TableInfo } from '@connect-services/client/services/add-layer.service';

type ResolvedFluxLayerInfo = {
    hasGeom: boolean | undefined;
    name: string;
    options?: LayerOptions;
    type: FluxLayerType;
    url: string;
};

export type ServiceCatalogResolutionError = {
    errorMessage: string;
    hasGeom: boolean | undefined;
    layerName: string;
    serviceCatalogId: string;
    sourceURL: string;
};

type ServiceCatalogResolutionResult = {
    error: ServiceCatalogResolutionError | null;
    resolvedEntry: [string, ResolvedFluxLayerInfo] | null;
};

type ResolveSelectedServiceCatalogsResult = {
    hasResolvedEntries: boolean;
    selectedTablesInfo: TableInfo[];
};

@Injectable({
    providedIn: 'root',
})
export class AddLayerFluxService {
    public static readonly acceptedProjections: string[] = ['EPSG:3857', 'EPSG:4326', 'EPSG:2154', 'EPSG:3945'];
    public static readonly validProjection = 'EPSG:3857';

    private static readonly layerNameToExclude = 'WMS layers';
    private static readonly tileSize = 256;

    public readonly availableSourceLayers = signal<UrlSourceLayer[]>([]);
    public readonly resolvedFluxLayerInfoByTableId = signal<Record<string, ResolvedFluxLayerInfo>>({});
    public readonly selectedFluxType = signal<FluxLayerType | undefined>(undefined);
    public readonly selectedServiceCatalogs = signal<ServiceCatalog[]>([]);
    public readonly serviceCatalogResolutionErrors = signal<ServiceCatalogResolutionError[]>([]);
    public sourceUrlControl?: FormControl<string>;

    public constructor(private readonly urlSourceService: UrlSourceService) {}

    public buildFluxLayerDto(
        tableInfo: TableInfo,
        selectedDeposit: string | undefined,
        isLockVersion: boolean,
    ): CreateLayerDtoWithoutProject {
        const resolvedFluxLayerInfo = this.resolvedFluxLayerInfoByTableId()[tableInfo.id];

        if (resolvedFluxLayerInfo) {
            return {
                businessIdentifier: null,
                deposit: selectedDeposit,
                hasGeom: resolvedFluxLayerInfo.hasGeom,
                isVisible: true,
                name: resolvedFluxLayerInfo.name,
                options: resolvedFluxLayerInfo.options,
                order: 1,
                type: resolvedFluxLayerInfo.type,
                url: resolvedFluxLayerInfo.url,
                versionLock: isLockVersion,
            };
        }

        const selectedFluxType = this.selectedFluxType();

        if (!selectedFluxType) {
            throw new Error('invalidLayerType');
        }

        return this.getLayerTypeFluxInfo(tableInfo, {
            businessIdentifier: null,
            deposit: selectedDeposit,
            hasGeom: tableInfo.hasGeom,
            isVisible: true,
            name: tableInfo.tableName,
            order: 1,
            type: selectedFluxType,
            versionLock: isLockVersion,
        });
    }

    public clearResolutionState(): void {
        this.resolvedFluxLayerInfoByTableId.set({});
        this.serviceCatalogResolutionErrors.set([]);
    }

    public ensureSourceUrlControl(): void {
        if (!this.sourceUrlControl) {
            this.sourceUrlControl = new FormControl('', { nonNullable: true });
        }
    }

    public async loadAvailableSourceLayers(url: string): Promise<void> {
        this.availableSourceLayers.set([]);
        this.clearResolutionState();

        if (!url.trim()) {
            this.sourceUrlControl?.setErrors(null);

            return;
        }

        const selectedLayerType = this.selectedFluxType();

        if (!selectedLayerType) {
            return;
        }

        this.urlSourceService.urlSourceType.set(selectedLayerType);
        await this.urlSourceService.load(url, selectedLayerType);

        const layers = this.urlSourceService
            .parse()
            .filter((layer: UrlSourceLayer) => layer.name !== AddLayerFluxService.layerNameToExclude)
            .filter((layer) =>
                selectedLayerType === UrlSourceEnum.WMS
                    ? !!layer.availableProjections &&
                      [...layer.availableProjections].some((crsProjection: string) =>
                          AddLayerFluxService.acceptedProjections.includes(crsProjection),
                      )
                    : true,
            );

        this.sourceUrlControl?.setErrors(null);
        this.availableSourceLayers.set(layers);
    }

    public reset(): void {
        this.selectedFluxType.set(undefined);
        this.selectedServiceCatalogs.set([]);
        this.availableSourceLayers.set([]);
        this.resolvedFluxLayerInfoByTableId.set({});
        this.serviceCatalogResolutionErrors.set([]);
        this.urlSourceService.detectedVersion.set(null);
        this.urlSourceService.urlSourceType.set(null);
        this.urlSourceService.wfsExtension.set(null);
        this.sourceUrlControl = undefined;
    }

    public async resolveSelectedServiceCatalogs(): Promise<ResolveSelectedServiceCatalogsResult> {
        this.serviceCatalogResolutionErrors.set([]);

        const resolutionResults: ServiceCatalogResolutionResult[] = await Promise.all(
            this.selectedServiceCatalogs().map((serviceCatalog: ServiceCatalog) =>
                this.resolveServiceCatalog(serviceCatalog),
            ),
        );

        const errors: ServiceCatalogResolutionError[] = resolutionResults.flatMap((result) =>
            result.error ? [result.error] : [],
        );
        const resolvedEntries: [string, ResolvedFluxLayerInfo][] = resolutionResults.flatMap((result) =>
            result.resolvedEntry ? [result.resolvedEntry] : [],
        );

        if (errors.length) {
            this.serviceCatalogResolutionErrors.set(errors);
        }

        this.resolvedFluxLayerInfoByTableId.set(Object.fromEntries(resolvedEntries));

        return {
            hasResolvedEntries: !!resolvedEntries.length,
            selectedTablesInfo: this.selectedServiceCatalogs()
                .filter((serviceCatalog: ServiceCatalog) =>
                    resolvedEntries.some(([serviceCatalogId]) => serviceCatalogId === serviceCatalog.id),
                )
                .map((serviceCatalog: ServiceCatalog) => ({
                    hasGeom: serviceCatalog.serviceType === ServiceCatalog.ServiceTypeEnum.WFS ? true : undefined,
                    id: serviceCatalog.id,
                    tableName: serviceCatalog.layerName,
                })),
        };
    }

    public selectServiceCatalogs(serviceCatalogs: ServiceCatalog[]): TableInfo[] {
        this.selectedServiceCatalogs.set(serviceCatalogs);
        this.clearResolutionState();

        return serviceCatalogs.map((serviceCatalog: ServiceCatalog) => ({
            hasGeom: serviceCatalog.serviceType === ServiceCatalog.ServiceTypeEnum.WFS ? true : undefined,
            id: serviceCatalog.id,
            tableName: serviceCatalog.layerName,
        }));
    }

    private findMatchingServiceCatalogLayer(serviceCatalog: ServiceCatalog): UrlSourceLayer | undefined {
        const normalize = (value: string | null | undefined): string => (value ?? '').trim().toLowerCase();
        const expectedServiceName = normalize(serviceCatalog.serviceName);
        const expectedLayerTitle = normalize(serviceCatalog.layerName);
        const parsedLayers = this.urlSourceService.parse();

        const serviceNameMatch = parsedLayers.find(
            (layer: UrlSourceLayer) => normalize(layer.name) === expectedServiceName,
        );

        if (serviceNameMatch) {
            return serviceNameMatch;
        }

        return parsedLayers.find((layer: UrlSourceLayer) => normalize(layer.title) === expectedLayerTitle);
    }

    private getLayerTypeFluxInfo(
        tableInfo: TableInfo,
        layerInfo: CreateLayerDtoWithoutProject,
    ): CreateLayerDtoWithoutProject {
        const sourceUrl = this.getSourceUrl(layerInfo.type, tableInfo.tableName);

        if (layerInfo.type === Layer.TypeEnum.WMS) {
            return { ...layerInfo, url: sourceUrl };
        }

        const wfsExtension: WfsExtensionEnum | null = this.urlSourceService.wfsExtension();

        return {
            ...layerInfo,
            hasGeom: true,
            url: sourceUrl,
            ...(wfsExtension !== null && { options: { wfsExtension } }),
        };
    }

    private getSourceUrl(layerType: Layer.TypeEnum, layerName: string): string {
        switch (layerType) {
            case Layer.TypeEnum.WMS:
                return this.urlSourceService.generateWMSUrlWithReplacementToken(
                    this.sourceUrlControl!.value,
                    layerName,
                    AddLayerFluxService.validProjection,
                    AddLayerFluxService.tileSize,
                );

            case Layer.TypeEnum.WFS:
                return this.urlSourceService.generateGetFeatureWFSUrl(this.sourceUrlControl!.value, layerName);
            default:
                return '';
        }
    }

    private async resolveServiceCatalog(serviceCatalog: ServiceCatalog): Promise<ServiceCatalogResolutionResult> {
        try {
            const type = serviceCatalog.serviceType.toLowerCase() as FluxLayerType;

            this.urlSourceService.urlSourceType.set(type);
            await this.urlSourceService.load(serviceCatalog.sourceUrl, type);

            const matchedLayer = this.findMatchingServiceCatalogLayer(serviceCatalog);

            if (!matchedLayer) {
                throw new Error('noFluxData');
            }

            const availableProjections = matchedLayer.availableProjections;
            const hasAcceptedProjection =
                !!availableProjections &&
                AddLayerFluxService.acceptedProjections.some((projection) => availableProjections.has(projection));

            if (type === Layer.TypeEnum.WMS && !hasAcceptedProjection) {
                throw new Error('noFluxData');
            }

            const url =
                type === Layer.TypeEnum.WMS
                    ? this.urlSourceService.generateWMSUrlWithReplacementToken(
                          serviceCatalog.sourceUrl,
                          serviceCatalog.serviceName,
                          AddLayerFluxService.validProjection,
                          AddLayerFluxService.tileSize,
                      )
                    : this.urlSourceService.generateGetFeatureWFSUrl(
                          serviceCatalog.sourceUrl,
                          serviceCatalog.serviceName,
                      );

            const wfsExtension = this.urlSourceService.wfsExtension();
            const options = type === Layer.TypeEnum.WFS && wfsExtension ? { wfsExtension } : undefined;

            return {
                error: null,
                resolvedEntry: [
                    serviceCatalog.id,
                    {
                        hasGeom: type === Layer.TypeEnum.WFS ? true : undefined,
                        name: serviceCatalog.layerName,
                        options,
                        type,
                        url,
                    } satisfies ResolvedFluxLayerInfo,
                ] as const,
            };
        } catch (error) {
            return {
                error: {
                    errorMessage: error instanceof Error ? error.message : 'errorDefault',
                    hasGeom: serviceCatalog.serviceType === ServiceCatalog.ServiceTypeEnum.WFS ? true : undefined,
                    layerName: serviceCatalog.layerName,
                    serviceCatalogId: serviceCatalog.id,
                    sourceURL: serviceCatalog.sourceUrl,
                } satisfies ServiceCatalogResolutionError,
                resolvedEntry: null,
            };
        }
    }
}
